import { getEditorEmptyJsonValue } from '@follow/farte'
import { call, put, select, take, takeEvery } from 'redux-saga/effects'
import { customHistory } from '../../../history'
import { uiSagasFactory } from '../../restux/ui/restuxUiSagas.factory'
import {
  managerDocumentTemplatesConfig,
  UiManagerDocumentTemplateActionTypes,
} from './managerDocumentTemplates.model'
import { documentTemplatesActions } from '../../cache/documentTemplates'
import { managerDocumentTemplatesActions } from './managerDocumentTemplates.actions'

import { RESTUX_IDENTIFIER } from '../../restux.identifier'
import { generateIdentifier } from '../../restux/restux.utilities'
import { inUseDocumentTemplateSelector } from '../bottomPanelDocumentTemplates'
import { RestuxLoadingState } from '../../restux/ui'
import { RestuxResources } from '../../resources'
import { editorFontFamilyPreferencesSelector } from '../../domain/me'
import { DocumentTemplate } from '../../../model/DocumentTemplate'
import { ApiResponse } from 'apisauce'
import { CustomFile, FileType } from '../../../model/File'
import { uploadFile } from '../../cache/files/api'
import { addResponseError } from '../../message/message.actions'

const paginationSagas = uiSagasFactory(managerDocumentTemplatesConfig)

function* addExistingQuestionToInUseDocumentTemplateWorker({
  documentTemplateId,
  selectedQuestion,
  onQuestionAddedToDocumentTemplate,
}: ReturnType<typeof managerDocumentTemplatesActions.addExistingQuestionToInUseDocumentTemplate>) {
  const taskIdentifier = generateIdentifier(RESTUX_IDENTIFIER.addExistingQuestionToDocumentTemplate)

  const inUseDocumentTemplate: ReturnType<typeof inUseDocumentTemplateSelector> = yield select(
    inUseDocumentTemplateSelector,
  )
  if (
    inUseDocumentTemplate &&
    inUseDocumentTemplate.id === documentTemplateId &&
    inUseDocumentTemplate.type === 'farte'
  ) {
    yield put(
      documentTemplatesActions.actions.apiUpdateItem(
        inUseDocumentTemplate.id,
        {
          variables: [...inUseDocumentTemplate.variables, selectedQuestion],
        },
        {
          identifier: taskIdentifier,
        },
      ),
    )
    const { identifier } = yield take(documentTemplatesActions.types.STORE_SET_ITEM_DETAILS)
    if (identifier === taskIdentifier) {
      onQuestionAddedToDocumentTemplate(selectedQuestion)
    }
  } else {
    throw new Error(`Inconsitence entre le document cible et celui en cours d'edition`)
  }
}

function* addExistingQuestionToInUseDocumentTemplateWatcher() {
  yield takeEvery(
    UiManagerDocumentTemplateActionTypes.ADD_EXISTING_QUESTION_TO_DOCUMENT_IN_USE_TEMPLATE,
    addExistingQuestionToInUseDocumentTemplateWorker,
  )
}

function* addQuestionnaireToInUseDocumentTemplateWorker({
  documentTemplateId,
  selectedQuestionnaire,
  onQuestionnaireAdded,
}: ReturnType<
  typeof managerDocumentTemplatesActions.addQuestionnaireTemplateToInUseDocumentTemplate
>) {
  const inUseDocumentTemplate: ReturnType<typeof inUseDocumentTemplateSelector> = yield select(
    inUseDocumentTemplateSelector,
  )
  if (
    inUseDocumentTemplate &&
    inUseDocumentTemplate.id === documentTemplateId &&
    inUseDocumentTemplate.type === 'farte'
  ) {
    const taskIdentifier = generateIdentifier(RestuxResources.documentTemplate)
    yield put(
      documentTemplatesActions.actions.apiUpdateItem(
        inUseDocumentTemplate.id,
        {
          questionnaires: [...inUseDocumentTemplate.questionnaires, selectedQuestionnaire],
        },
        { identifier: taskIdentifier },
      ),
    )
    const { identifier }: ReturnType<typeof documentTemplatesActions.actions.storeSetItemDetails> =
      yield take(documentTemplatesActions.types.STORE_SET_ITEM_DETAILS)
    if (taskIdentifier === identifier) {
      onQuestionnaireAdded(selectedQuestionnaire)
    }
  } else {
    throw new Error(`Inconsitence entre le document cible et celui en cours d'edition`)
  }
}

function* addQuestionnaireToInUseDocumentTemplateWatcher() {
  yield takeEvery(
    UiManagerDocumentTemplateActionTypes.ADD_QUESTIONNAIRE_TO_IN_USE_DOCUMENT_TEMPLATE,
    addQuestionnaireToInUseDocumentTemplateWorker,
  )
}

function* createDocumentTemplateWorker({
  templateResources,
}: ReturnType<typeof managerDocumentTemplatesActions.createDocumentTemplate>) {
  const { selectedCategory, documentType } = templateResources

  const taskIdentifier = generateIdentifier(RESTUX_IDENTIFIER.createDocumentTemplateSaga)
  const editorFontFamily: ReturnType<typeof editorFontFamilyPreferencesSelector> = yield select(
    editorFontFamilyPreferencesSelector,
  )

  let documentTemplate: Partial<DocumentTemplate> = {
    title: 'Nouveau modèle',
    category: selectedCategory,
  }

  if (documentType === 'pdf') {
    const response: ApiResponse<CustomFile> = yield call(
      uploadFile,
      templateResources.file,
      FileType.MEDICAL_EVENT,
    )

    if (!response.ok || !response.data) {
      yield put(addResponseError(response))
    } else {
      documentTemplate = {
        ...documentTemplate,
        type: 'pdf',
        fileId: response.data.id,
      }
    }
  } else {
    const template = getEditorEmptyJsonValue(editorFontFamily)
    documentTemplate = {
      ...documentTemplate,
      type: 'farte',
      template,
    }
  }

  yield put(
    documentTemplatesActions.actions.apiCreateItem(documentTemplate, {
      identifier: taskIdentifier,
    }),
  )
  const {
    identifier,
    item,
  }: ReturnType<typeof documentTemplatesActions.actions.storeSetItemDetails> = yield take(
    documentTemplatesActions.types.STORE_SET_ITEM_DETAILS,
  )
  if (identifier === taskIdentifier) {
    yield put(managerDocumentTemplatesActions.useId(item.id))
    customHistory.navigate(`/managers/documents/${item.id}`)
  }
}

function* createDocumentTemplateWatcher() {
  yield takeEvery(UiManagerDocumentTemplateActionTypes.CREATE, createDocumentTemplateWorker)
}

function* updateDocumentTemplateWorker({
  documentTemplateId,
  documentTemplateUpdates,
}: ReturnType<typeof managerDocumentTemplatesActions.updateDocumentTemplate>) {
  const taskIdentifier = generateIdentifier(RESTUX_IDENTIFIER.updateDocumentTemplateSaga)
  yield put(managerDocumentTemplatesActions.setInUseLoading(RestuxLoadingState.RELOADING))
  yield put(
    documentTemplatesActions.actions.apiUpdateItem(documentTemplateId, documentTemplateUpdates, {
      identifier: taskIdentifier,
    }),
  )
  const { identifier }: ReturnType<typeof documentTemplatesActions.actions.storeSetItemDetails> =
    yield take(documentTemplatesActions.types.STORE_SET_ITEM_DETAILS)
  if (identifier === taskIdentifier) {
    yield put(managerDocumentTemplatesActions.setInUseLoading(RestuxLoadingState.IDLE))
  }
}

function* updateDocumentTemplateWatcher() {
  yield takeEvery(UiManagerDocumentTemplateActionTypes.UPDATE, updateDocumentTemplateWorker)
}

export const managerDocumentTemplatesSagas = {
  ...paginationSagas,
  addExistingQuestionToInUseDocumentTemplateWatcher,
  addQuestionnaireToInUseDocumentTemplateWatcher,
  createDocumentTemplateWatcher,
  updateDocumentTemplateWatcher,
}
